import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  PartyPopper, ShoppingCart, ArrowRight, Gift, Check, Star, Phone
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RentalPackagesPage = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/rental-packages`);
      setPackages(response.data);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const addPackageToCart = (pkg) => {
    // Convert package items to rental cart format
    const rentalItems = pkg.items.map(item => ({
      rental_id: item.rental_id,
      rental_name: item.rental_name,
      quantity: item.quantity,
      price_per_day: Math.round(pkg.discounted_price / pkg.items.length / item.quantity),
      days: 1
    }));

    // Get existing cart or create new
    const existingCart = JSON.parse(localStorage.getItem("rentalCart") || "[]");
    
    // Add package items to cart
    rentalItems.forEach(newItem => {
      const existing = existingCart.find(item => item.rental_id === newItem.rental_id);
      if (existing) {
        existing.quantity += newItem.quantity;
      } else {
        existingCart.push(newItem);
      }
    });

    localStorage.setItem("rentalCart", JSON.stringify(existingCart));
    toast.success(`${pkg.name} added to cart!`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Gift className="w-10 h-10 text-primary" />
            <span className="font-heading text-2xl font-semibold text-foreground">Package Deals</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/rentals">
              <Button variant="outline">All Rentals</Button>
            </Link>
            <Link to="/order">
              <Button className="bg-primary">
                <ShoppingCart className="w-5 h-5 mr-2" />
                Order Now
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-2xl md:text-3xl font-bold text-foreground mb-2">Sis Carol</p>
          <div className="inline-flex items-center gap-2 bg-white/80 rounded-full px-4 py-2 shadow-sm mb-6">
            <Phone className="w-4 h-4 text-primary" />
            <span className="text-primary font-medium">1876-277-2648</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-4">
            Party Package Deals 🎁
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
            Save up to 20% with our curated party packages! Everything you need bundled together.
          </p>
          <Badge className="bg-green-500 text-white text-lg py-2 px-6">
            SAVE UP TO 20% OFF
          </Badge>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading packages...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.map((pkg) => (
                <Card key={pkg.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 hover:border-primary" data-testid={`package-${pkg.id}`}>
                  {/* Package Image */}
                  <div className="h-48 overflow-hidden relative">
                    {pkg.image_url ? (
                      <img 
                        src={pkg.image_url} 
                        alt={pkg.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                        <Gift className="w-16 h-16 text-white" />
                      </div>
                    )}
                    <Badge className="absolute top-4 right-4 bg-red-500 text-white text-sm">
                      {pkg.discount_percent}% OFF
                    </Badge>
                  </div>

                  {/* Package Details */}
                  <div className="p-6">
                    <h3 className="font-heading text-2xl font-bold mb-2">{pkg.name}</h3>
                    <p className="text-muted-foreground mb-4">{pkg.description}</p>

                    {/* Included Items */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <p className="font-semibold text-sm text-gray-600 mb-2">INCLUDES:</p>
                      <ul className="space-y-1">
                        {pkg.items.map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>{item.quantity}x {item.rental_name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Pricing */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-muted-foreground line-through">
                          J${pkg.original_price.toLocaleString()}
                        </p>
                        <p className="font-heading text-3xl text-primary font-bold">
                          J${pkg.discounted_price.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">per day</p>
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        Save J${(pkg.original_price - pkg.discounted_price).toLocaleString()}
                      </Badge>
                    </div>

                    {/* Add to Cart Button */}
                    <Button 
                      className="w-full bg-primary hover:bg-primary/90"
                      onClick={() => addPackageToCart(pkg)}
                    >
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Add Package to Cart
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-pink-500 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Need a Custom Package? 📞
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Call us to create a custom package tailored to your event!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/rentals">
              <Button className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg font-bold">
                Browse All Rentals <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:18762772648">
              <Button variant="outline" className="border-white text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg font-bold">
                <Phone className="mr-2 w-5 h-5" /> Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white/70">
            © 2026 Sis Carol Cakes & Party Rentals | Irie Eatz Restaurant, Market Street, Ocho Rios
          </p>
          <p className="text-white/70 mt-2">
            <Phone className="w-4 h-4 inline mr-1" /> 1876-277-2648
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RentalPackagesPage;
